import { Fragment, useCallback, useEffect, useRef, useState } from "react";

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Stack,
} from "@mui/material";
import {
  DeleteForeverOutlined,
  MicOutlined,
  DescriptionOutlined,
  EditOutlined,
} from "@mui/icons-material";

import { Vid } from "../../../../types/skills";
import { SkillTemplate } from "../../../../src/API";
import { useSkillTemplates } from "../../../../providers/state/skillTemplate";

import VideoViewDialog from "../../video-view-dialog";
import Recorder from "../../recorder";
import ConfirmDialog from "../../confirmation";
import TextFieldPrompt from "../../text-field-prompt";
import FileDropzone from "../../file-dropzone";

import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import { useAuth0 } from "@auth0/auth0-react";
import { useProfile } from "../../../../providers/profile";
import { idFetcher } from "../../../../utilities/fetcher";
import { useSnackbar } from "notistack";

const TutorialVideos = ({
  template,
  refresh,
  editable,
}: {
  template: Partial<SkillTemplate>;
  refresh: () => Promise<void>;
  editable: boolean;
}) => {
  const [tutorialVideos, setTutorialVideos] = useState<Vid[]>([]);
  const [viewVid, setViewVid] = useState<Vid>();
  const [viewVidOpen, setViewVidOpen] = useState(false);
  const [editVidName, setEditVidName] = useState("");
  const [editVidNameOpen, setEditVidNameOpen] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [recorderOpen, setRecorderOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const deleteAction = useRef<() => void>();

  const { upsertSkillTemplate } = useSkillTemplates();

  const { getIdTokenClaims } = useAuth0();
  const { account } = useProfile();
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    setTutorialVideos(JSON.parse(template.tutorialVideos || "[]"));
  }, [template]);

  const addVid = useCallback(
    async ({ urls }: { urls: [string] }) => {
      const add = {
        title: "",
        when: new Date().valueOf(),
        urls,
        description: "",
      };
      const result = await upsertSkillTemplate({
        id: template.id,
        tutorialVideos: JSON.stringify([...tutorialVideos, add]),
      });
      await refresh();
      return result;
    },
    [refresh, template, tutorialVideos, upsertSkillTemplate]
  );

  const deleteVid = useCallback(
    async (vid: any) => {
      const result = await upsertSkillTemplate({
        id: template.id,
        tutorialVideos: JSON.stringify(tutorialVideos.filter((v) => v !== vid)),
      });
      await refresh();
      return result;
    },
    [refresh, template, tutorialVideos, upsertSkillTemplate]
  );

  const openVid = useCallback(async (vid: any) => {
    setViewVid(vid);
    setViewVidOpen(true);
  }, []);

  const openEditVidName = useCallback((vid: any) => {
    setViewVid(vid);
    setEditVidName(vid.title);
    setEditVidNameOpen(true);
  }, []);

  const saveEditVidName = useCallback(async () => {
    if (!viewVid) return setEditVidNameOpen(false);

    const foundVid = tutorialVideos.find((item) => item.when === viewVid.when);
    if (foundVid) {
      foundVid.title = editVidName;

      const result = await upsertSkillTemplate({
        id: template.id,
        tutorialVideos: JSON.stringify(tutorialVideos),
      });

      await refresh();
      setEditVidNameOpen(false);
      return result;
    }
  }, [
    editVidName,
    refresh,
    template,
    tutorialVideos,
    upsertSkillTemplate,
    viewVid,
  ]);

  const onDocDrop = useCallback(
    async (acceptedFiles: any[]) => {
      if (!account) return;

      acceptedFiles.forEach(async (file: any) => {
        try {
          const ext = file.name.split(".").pop();
          const key = encodeURIComponent(
            `protected/${account.sub}/${uuidv4()}.${ext}`
          );
          // console.log(key);
          const upload = await idFetcher(
            `/api/s3/put/${key}`,
            getIdTokenClaims
          );
          // console.log(upload.signedUrl);

          const uploadResult = await axios.put(upload.signedUrl, file, {
            headers: { "content-type": file.type },
            onUploadProgress: (evt) => {
              if (!evt.total) return setUploadProgress(0);
              setUploadProgress((evt.loaded / evt.total) * 100);
            },
          });
          // console.log(uploadResult);

          const add = {
            title: file.name,
            when: new Date().valueOf(),
            urls: [key],
          };
          const result = await upsertSkillTemplate({
            id: template.id,
            tutorialVideos: JSON.stringify([...tutorialVideos, add]),
          });

          await refresh();
          return result;
        } catch (e) {
          console.error(e);
          enqueueSnackbar("Problem with file upload. Please try again.", {
            variant: "error",
          });
        }
      });
    },
    [
      account,
      getIdTokenClaims,
      upsertSkillTemplate,
      template,
      tutorialVideos,
      refresh,
      enqueueSnackbar,
    ]
  );

  const handleDelete = useCallback(() => {
    if (deleteAction.current) deleteAction.current();
    deleteAction.current = undefined;
  }, []);

  return (
    <>
      <Card>
        <CardHeader
          action={
            editable ? (
              <Button
                endIcon={<MicOutlined />}
                color="error"
                size="small"
                variant="contained"
                onClick={() => setRecorderOpen(true)}
              >
                Record
              </Button>
            ) : null
          }
          title="TUTORIAL VIDEOS"
          sx={{ "& span": { fontSize: "1rem" } }}
        />
        <CardContent>
          {editable ? (
            <Alert severity="info" sx={{ py: 0, fontWeight: 600 }}>
              All students can see these videos. To leave a video for a specific
              student, please use a Personal Tutorial instead.
            </Alert>
          ) : null}
          <List
            sx={{
              maxWidth: "34rem",
              mx: "auto",
            }}
            dense={true}
          >
            {tutorialVideos.map((vid) => (
              <Fragment key={vid.when}>
                <Divider />
                <ListItem sx={{ alignItems: "center" }}>
                  <ListItemText
                    sx={{ "& span": { fontWeight: 700 } }}
                    primary={vid.title || "Untitled"}
                    secondary={`${new Date(
                      vid.when
                    ).toLocaleDateString()} ${new Date(
                      vid.when
                    ).toLocaleTimeString()}`}
                  />
                  <Box sx={{ float: "right", display: "flex" }}>
                    <Button
                      onClick={() => {
                        openVid(vid);
                      }}
                      color="secondary"
                      endIcon={<DescriptionOutlined />}
                      size="small"
                      variant="outlined"
                    >
                      Open
                    </Button>
                    {editable ? (
                      <>
                        <IconButton
                          onClick={() => openEditVidName(vid)}
                          aria-label="edit"
                          size="small"
                          sx={{ marginLeft: "0.5rem" }}
                        >
                          <EditOutlined />
                        </IconButton>
                        <IconButton
                          onClick={() => {
                            deleteAction.current = () => deleteVid(vid);
                            setDeleteOpen(true);
                          }}
                          aria-label="delete"
                          size="small"
                        >
                          <DeleteForeverOutlined />
                        </IconButton>
                      </>
                    ) : null}
                  </Box>
                </ListItem>
              </Fragment>
            ))}
          </List>
          {editable ? (
            <>
              <FileDropzone onDrop={onDocDrop} />
              <LinearProgress
                variant="determinate"
                value={uploadProgress}
                sx={{ height: "8px", marginTop: "1rem" }}
              />
            </>
          ) : null}
        </CardContent>
      </Card>
      <Recorder
        open={recorderOpen}
        setOpen={setRecorderOpen}
        onSave={addVid}
        isPrivate={false}
      />
      <VideoViewDialog
        open={viewVidOpen}
        setOpen={setViewVidOpen}
        vid={viewVid}
      />
      <TextFieldPrompt
        open={editVidNameOpen}
        setOpen={setEditVidNameOpen}
        value={editVidName}
        setValue={setEditVidName}
        onSave={saveEditVidName}
      />
      <ConfirmDialog
        title="Delete Tutorial Video?"
        open={deleteOpen}
        setOpen={setDeleteOpen}
        onConfirm={handleDelete}
      >
        Are you sure you want to delete?
      </ConfirmDialog>
    </>
  );
};

export default TutorialVideos;
